import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import type { TransferMatchProfile } from '#banksync/transfer-profiles';
import { AccountAutocomplete } from '#components/autocomplete/AccountAutocomplete';
import { PayeeAutocomplete } from '#components/autocomplete/PayeeAutocomplete';
import { Modal, ModalCloseButton, ModalHeader } from '#components/common/Modal';

type Props = {
  profile: TransferMatchProfile;
  onSave: (profile: TransferMatchProfile) => void;
};

export function EditTransferMatchProfileModal({ profile, onSave }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState(profile.name);
  const [payeeIds, setPayeeIds] = useState(profile.payeeIds);
  const [accountIds, setAccountIds] = useState(profile.accountIds);
  const [windowDays, setWindowDays] = useState(profile.windowDays);
  const [enabled, setEnabled] = useState(profile.enabled);

  const clampWindow = (n: number) => Math.max(0, Math.min(14, n));

  return (
    <Modal name="edit-transfer-match-profile">
      {({ state }) => (
        <View style={{ width: 'min(620px, 92vw)', flexShrink: 0 }}>
          <ModalHeader
            title={t('Transfer matching profile')}
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />

          <View style={{ gap: 12, padding: 8, flexShrink: 0 }}>
            <View style={{ flexShrink: 0 }}>
              <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
                <Trans>Name</Trans>
              </Text>
              <Input value={name} onChangeValue={setName} />
            </View>

            <View style={{ flexShrink: 0 }}>
              <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
                <Trans>Allowed payees (both sides must match)</Trans>
              </Text>
              <PayeeAutocomplete
                type="multi"
                value={payeeIds}
                onSelect={(ids: string[]) => setPayeeIds(ids)}
                showMakeTransfer={false}
                embedded
              />
            </View>

            <View style={{ flexShrink: 0 }}>
              <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
                <Trans>Limit to accounts (optional)</Trans>
              </Text>
              <AccountAutocomplete
                type="multi"
                value={accountIds}
                onSelect={(ids: string[]) => setAccountIds(ids)}
                embedded
              />
            </View>

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                flexShrink: 0,
              }}
            >
              <Text style={{ fontSize: 13 }}>
                <Trans>Match within</Trans>
              </Text>
              <Button
                isDisabled={windowDays <= 0}
                onPress={() => setWindowDays(w => clampWindow(w - 1))}
              >
                -
              </Button>
              <Text style={{ minWidth: 70, textAlign: 'center' }}>
                <Trans count={windowDays}>± {{ count: windowDays }} days</Trans>
              </Text>
              <Button
                isDisabled={windowDays >= 14}
                onPress={() => setWindowDays(w => clampWindow(w + 1))}
              >
                +
              </Button>
            </View>

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexShrink: 0,
              }}
            >
              <input
                type="checkbox"
                checked={enabled}
                onChange={e => setEnabled(e.target.checked)}
              />
              <Text style={{ fontSize: 13 }}>
                <Trans>Auto-link after each bank sync</Trans>
              </Text>
            </label>

            {payeeIds.length === 0 && (
              <Text
                style={{
                  fontSize: 12,
                  color: theme.warningText,
                  flexShrink: 0,
                }}
              >
                <Trans>
                  Without allowed payees this profile will not auto-link.
                </Trans>
              </Text>
            )}
          </View>

          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: 8,
              padding: 8,
              flexShrink: 0,
            }}
          >
            <Button onPress={() => state.close()}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              variant="primary"
              isDisabled={name.trim() === ''}
              onPress={() => {
                onSave({
                  ...profile,
                  name: name.trim(),
                  payeeIds,
                  accountIds,
                  windowDays,
                  enabled,
                });
                state.close();
              }}
            >
              <Trans>Save</Trans>
            </Button>
          </View>
        </View>
      )}
    </Modal>
  );
}
